import React, { useState } from "react";
import moment from "moment";
import { useGetMyCalendarQuery } from "../../../../store/appApi";
import PageTitle from "../../../layouts/PageTitle";
import { formatWorkDuration } from "./attendanceUtils";
import ManualEntryNote, { ManualMark } from "./ManualEntryNote";

const COLOR_MAP = {
  PRESENT: "#2bc155",
  PARTIAL: "#a855f7",
  ON_LEAVE: "#3f9ae0",
  ON_LEAVE_FIRST_HALF: "#0ea5e9",
  ON_LEAVE_SECOND_HALF: "#0284c7",
  HALF_DAY_LEAVE: "#14b8a6",
  HALF_DAY_PRESENT: "#f59e0b",
  PENDING: "#ff9f43",
  PENDING_FIRST_HALF: "#ff9f43",
  PENDING_SECOND_HALF: "#ff9f43",
  ABSENT: "#f32d2d",
  UPCOMING: "#64748b",
  HOLIDAY: "#8b5cf6",
};

const TAG_LABEL = {
  HOLIDAY: "Holiday",
  PRESENT: "Present",
  PARTIAL: "Partial",
  ON_LEAVE: "On Leave",
  ON_LEAVE_FIRST_HALF: "On Leave (First Half)",
  ON_LEAVE_SECOND_HALF: "On Leave (Second Half)",
  HALF_DAY_LEAVE: "Half Day Leave",
  HALF_DAY_PRESENT: "First Half Day",
  PENDING: "Leave Pending",
  PENDING_FIRST_HALF: "Leave Pending (First Half)",
  PENDING_SECOND_HALF: "Leave Pending (Second Half)",
  ABSENT: "Absent",
  UPCOMING: "Upcoming",
};

const PRESENT_COLOR = "#2bc155";

const toMonthStr = (d) => {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${m}`;
};

const MyCalendar = () => {
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const month = toMonthStr(monthDate);
  const { data, isLoading } = useGetMyCalendarQuery(month);

  const days = data?.days || [];
  const summary = data?.summary || {};

  // Offset blank cells so day 1 lands on the correct weekday column (Sun–Sat)
  const firstDay = new Date(
    Date.UTC(monthDate.getFullYear(), monthDate.getMonth(), 1),
  ).getUTCDay();
  const blanks = Array(firstDay).fill(null);

  const sel = selectedDay && days.find((d) => d.date === selectedDay);

  const prev = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() - 1);
    setMonthDate(d);
    setSelectedDay(null);
  };
  const next = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + 1);
    setMonthDate(d);
    setSelectedDay(null);
  };

  return (
    <>
      <PageTitle activeMenu="My Calendar" motherMenu="Attendance" />

      {/* Shrinks the day grid on small screens instead of letting it overflow
          the viewport — inline styles below only carry per-day dynamic color,
          everything size-related lives here so it can respond to width. */}
      <style>{`
        .mycal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px; }
        .mycal-weekday { padding: 8px 0; }
        .mycal-day { border-radius: 6px; padding: 8px 6px; min-height: 64px; }
        .mycal-day-num { font-size: 15px; }
        .mycal-day-tag { font-size: 11px; margin-top: 4px; }
        .mycal-day-duration { font-size: 10px; }

        @media (max-width: 576px) {
          .mycal-grid { gap: 3px; }
          .mycal-weekday { padding: 4px 0; font-size: 10px !important; }
          .mycal-day { padding: 4px 2px; min-height: 46px; border-radius: 4px; border-left-width: 3px !important; }
          .mycal-day-num { font-size: 12px; }
          .mycal-day-tag { font-size: 7px; margin-top: 2px; line-height: 1.1; }
          .mycal-day-duration { font-size: 6px; }
        }
        @media (max-width: 400px) {
          .mycal-day-tag { display: none; }
        }
      `}</style>

      {/* Stats row */}
      <div className="row g-2 mb-3">
        {[
          { label: "Present", key: "present", tag: "PRESENT" },
          { label: "Partial", key: "partial", tag: "PARTIAL" },
          {
            label: "First Half Day",
            key: "halfDayPresent",
            tag: "HALF_DAY_PRESENT",
          },
          { label: "Half Day Leave", key: "halfDay", tag: "HALF_DAY_LEAVE" },
          { label: "On Leave", key: "onLeave", tag: "ON_LEAVE" },
          { label: "Leave Pending", key: "pending", tag: "PENDING" },
          { label: "Absent", key: "absent", tag: "ABSENT" },
        ].map((s) => (
          <div className="col-6 col-sm" key={s.key}>
            <div
              className="card text-center py-4 mb-0"
              style={{
                borderLeft: `4px solid ${COLOR_MAP[s.tag]}`,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
              }}
            >
              <h4 className="mb-0">{summary[s.key] ?? 0}</h4>
              <small className="text-muted" style={{ fontSize: "16px" }}>
                {s.label}
              </small>
            </div>
          </div>
        ))}
      </div>

      {/* Full-width 7-column calendar card */}
      <div className="card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <button className="btn btn-sm btn-outline-secondary" onClick={prev}>
            &lsaquo;
          </button>
          <h5 className="mb-0">
            {monthDate.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </h5>
          <button className="btn btn-sm btn-outline-secondary" onClick={next}>
            &rsaquo;
          </button>
        </div>

        <div className="card-body p-3">
          {isLoading ? (
            <div className="text-center py-5">Loading…</div>
          ) : (
            <div className="mycal-grid">
              {/* Day-of-week headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div
                  key={d}
                  className="mycal-weekday text-center fw-bold small text-muted"
                  style={{ borderBottom: "2px solid #eee" }}
                >
                  {d}
                </div>
              ))}

              {/* Blank offset cells */}
              {blanks.map((_, i) => (
                <div key={`b${i}`} />
              ))}

              {days.map((day) => {
                const isHalfDay = day.tag === "HALF_DAY_LEAVE";
                const isUpcoming = day.tag === "UPCOMING";
                const color = COLOR_MAP[day.tag] || "#ccc";
                const isSelected = selectedDay === day.date;

                return (
                  <div
                    key={day.date}
                    className="mycal-day"
                    onClick={() =>
                      !isUpcoming &&
                      setSelectedDay(isSelected ? null : day.date)
                    }
                    style={{
                      background: isHalfDay
                        ? `linear-gradient(135deg, ${color}33 50%, ${PRESENT_COLOR}33 50%)`
                        : `${color}22`,
                      border: isSelected
                        ? `2px solid ${color}`
                        : `1px solid ${color}44`,
                      borderLeft: `4px solid ${color}`,
                      borderRight: isHalfDay
                        ? `4px solid ${PRESENT_COLOR}`
                        : undefined,
                      cursor: isUpcoming ? "default" : "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      opacity: isUpcoming ? 0.7 : 1,
                      transition: "box-shadow .15s",
                      boxShadow: isSelected ? `0 0 0 2px ${color}55` : "none",
                    }}
                  >
                    <div
                      className="mycal-day-num fw-bold"
                      style={{ color: "#333" }}
                    >
                      {parseInt(day.date.split("-")[2])}
                      {day.shortLeave && (
                        <span
                          title={
                            day.shortLeave.from && day.shortLeave.to
                              ? `Short leave ${day.shortLeave.from}–${day.shortLeave.to}`
                              : "Short leave"
                          }
                          style={{
                            marginLeft: 3,
                            fontSize: 9,
                            fontWeight: 700,
                            color: "#8b5cf6",
                          }}
                        >
                          ⏱{day.shortLeave.hours ? day.shortLeave.hours + "h" : ""}
                        </span>
                      )}
                      <ManualMark entry={day.manualEntry} />
                    </div>
                    <div
                      className="mycal-day-tag"
                      style={{
                        fontWeight: 700,
                        color: day.holiday ? "#8b5cf6" : color,
                        textAlign: "center",
                      }}
                    >
                      {day.holiday || TAG_LABEL[day.tag] || day.tag}
                    </div>
                    {day.workDurationMinutes != null && (
                      <div
                        className="mycal-day-duration"
                        style={{
                          color: "#6b7280",
                          textAlign: "center",
                        }}
                      >
                        {formatWorkDuration(day.workDurationMinutes)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="card-footer d-flex flex-wrap gap-3">
          {Object.entries(TAG_LABEL).map(([tag, label]) => (
            <span key={tag} className="d-flex align-items-center gap-1 small">
              {tag === "HALF_DAY_LEAVE" ? (
                <span
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 3,
                    display: "inline-block",
                    flexShrink: 0,
                    background: `linear-gradient(135deg, ${COLOR_MAP[tag]} 50%, ${PRESENT_COLOR} 50%)`,
                  }}
                />
              ) : (
                <span
                  style={{
                    width: 11,
                    height: 11,
                    background: COLOR_MAP[tag],
                    borderRadius: 3,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
              )}
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Day detail panel */}
      {sel && (
        <div
          className="card mt-3"
          style={{ borderTop: `4px solid ${COLOR_MAP[sel.tag]}` }}
        >
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <h6 className="mb-0">{moment(sel.date).format("DD MMM YYYY")}</h6>
              <span
                className="badge"
                style={{ background: COLOR_MAP[sel.tag] }}
              >
                {TAG_LABEL[sel.tag]}
              </span>
              {sel.workDurationMinutes != null && (
                <small className="text-muted">
                  {formatWorkDuration(sel.workDurationMinutes)}
                </small>
              )}
            </div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setSelectedDay(null)}
            >
              ✕
            </button>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {sel.punchIn && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Punch In</small>
                  <strong>{new Date(sel.punchIn).toLocaleTimeString()}</strong>
                </div>
              )}
              {sel.punchOut && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Punch Out</small>
                  <strong>{new Date(sel.punchOut).toLocaleTimeString()}</strong>
                </div>
              )}
              {sel.workDurationMinutes != null && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Work Hours</small>
                  <strong>{formatWorkDuration(sel.workDurationMinutes)}</strong>
                </div>
              )}
              {sel.distanceFromSite != null && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">
                    Distance from Site
                  </small>
                  <strong>{sel.distanceFromSite}m</strong>
                </div>
              )}
              {sel.leave?.leaveType && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Leave Type</small>
                  <strong>{sel.leave.leaveType}</strong>
                </div>
              )}
              {sel.shortLeave && (
                <div className="col-sm-6">
                  <small className="text-muted d-block">Short Leave (intimation)</small>
                  <strong>
                    {sel.shortLeave.from && sel.shortLeave.to
                      ? `${sel.shortLeave.from} – ${sel.shortLeave.to}`
                      : ""}
                    {sel.shortLeave.hours ? ` · ${sel.shortLeave.hours}h` : ""}
                  </strong>
                  {sel.shortLeave.reason && (
                    <div className="text-muted small">{sel.shortLeave.reason}</div>
                  )}
                </div>
              )}
              {sel.selfieUrl && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Punch-In Selfie</small>
                  <img
                    src={sel.selfieUrl}
                    alt="selfie"
                    className="img-fluid rounded mt-1"
                    style={{ maxHeight: 100 }}
                  />
                </div>
              )}
              {sel.punchOutSelfieUrl && (
                <div className="col-sm-3">
                  <small className="text-muted d-block">Punch-Out Selfie</small>
                  <img
                    src={sel.punchOutSelfieUrl}
                    alt="selfie"
                    className="img-fluid rounded mt-1"
                    style={{ maxHeight: 100 }}
                  />
                </div>
              )}
            </div>
            <ManualEntryNote entry={sel.manualEntry} />
          </div>
        </div>
      )}
    </>
  );
};

export default MyCalendar;
